import { useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { inventoryApi } from '../../api';
import {
  PageHeader,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';

/**
 * Bulk operations — FR-INV-01.10 / 01.11 / 01.12
 * CSV import, CSV export, and barcode-label PDF generation — three tools on
 * one page with clear visual separation per tool.
 */
export function BulkIoPage() {
  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Bulk operations"
        subtitle="Import, export, and label-printing tools for high-volume catalog work."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CsvExportCard />
        <CsvImportCard />
        <BarcodeLabelsCard />
      </div>
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================
// Export
// ============================================================
function CsvExportCard() {
  const mut = useMutation({
    mutationFn: () => inventoryApi.exportProductsCsv(),
    onSuccess: (blob) => {
      triggerDownload(blob, `products-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Export ready');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-sage-100 text-sage-700 flex items-center justify-center flex-shrink-0">
          <DownloadIcon />
        </div>
        <div>
          <div className="eyebrow text-ink-500">Export</div>
          <h3 className="font-semibold text-lg text-ink-900 mt-0.5">Products to CSV</h3>
        </div>
      </div>
      <p className="text-sm text-ink-600 mb-5 leading-relaxed">
        Download all products as a CSV. Open in Excel or Google Sheets, edit, and re-import using the Import tool.
      </p>
      <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
        {mut.isPending ? 'Preparing…' : 'Download CSV'}
      </button>
    </div>
  );
}

// ============================================================
// Import
// ============================================================
function CsvImportCard() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [abortOnError, setAbortOnError] = useState(false);
  const [result, setResult] = useState<any>(null);

  const importMut = useMutation({
    mutationFn: () => inventoryApi.importProductsCsv(file!, abortOnError),
    onSuccess: (data) => {
      setResult(data);
      toast.success(`Import complete · ${data.inserted} new · ${data.updated} updated · ${data.errors.length} errors`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const templateMut = useMutation({
    mutationFn: () => inventoryApi.downloadImportTemplate(),
    onSuccess: (blob) => triggerDownload(blob, 'products-import-template.csv'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
          <UploadIcon />
        </div>
        <div>
          <div className="eyebrow text-ink-500">Import</div>
          <h3 className="font-semibold text-lg text-ink-900 mt-0.5">Products from CSV</h3>
        </div>
      </div>
      <p className="text-sm text-ink-600 mb-4 leading-relaxed">
        Upload a CSV. Existing SKUs get updated, new ones get inserted.
      </p>

      <div className="mb-4">
        <button
          className="text-xs font-medium text-sage-700 hover:text-sage-800 transition-colors"
          onClick={() => templateMut.mutate()}
          disabled={templateMut.isPending}
        >
          {templateMut.isPending ? 'Preparing…' : '↓ Download template'}
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".csv"
        className="block w-full text-sm text-ink-700 mb-3
          file:mr-4 file:py-2 file:px-4 file:rounded-md
          file:border-0 file:text-sm file:font-semibold
          file:bg-canvas-subtle file:text-ink-700
          hover:file:bg-ink-100 file:transition-colors
          file:cursor-pointer cursor-pointer"
        onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }}
      />

      <label className="flex items-center gap-2 text-sm text-ink-700 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={abortOnError}
          onChange={(e) => setAbortOnError(e.target.checked)}
          className="accent-sage-500"
        />
        <span>Abort on first error <span className="text-ink-500">(default: skip bad rows)</span></span>
      </label>

      <button
        className="btn-primary"
        disabled={!file || importMut.isPending}
        onClick={() => importMut.mutate()}
      >
        {importMut.isPending ? 'Importing…' : 'Import'}
      </button>

      {result && (
        <div className="mt-5 pt-5 border-t border-ink-100">
          <div className="eyebrow text-ink-500 mb-3">Result</div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <ResultTile label="Total" value={result.total} />
            <ResultTile label="Inserted" value={result.inserted} tone="sage" />
            <ResultTile label="Updated" value={result.updated} tone="blue" />
            <ResultTile label="Errors" value={result.errors.length} tone={result.errors.length > 0 ? 'danger' : 'neutral'} />
          </div>
          {result.errors.length > 0 && (
            <div>
              <div className="eyebrow text-danger mb-2">Errors</div>
              <div className="max-h-40 overflow-y-auto bg-red-50 border border-red-200 rounded-xl p-3">
                <ul className="text-xs text-red-700 space-y-1">
                  {result.errors.slice(0, 50).map((err: any, i: number) => (
                    <li key={i}>
                      <span className="font-mono font-semibold">Row {err.row}</span>
                      {err.sku && <span className="text-red-600"> ({err.sku})</span>}:{' '}
                      {err.message}
                    </li>
                  ))}
                  {result.errors.length > 50 && (
                    <li className="text-red-500 italic">…and {result.errors.length - 50} more</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultTile({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'neutral' | 'sage' | 'blue' | 'danger' }) {
  const color = {
    neutral: 'text-ink-900',
    sage:    'text-sage-700',
    blue:    'text-blue-700',
    danger:  'text-danger',
  }[tone];
  return (
    <div className="bg-canvas-subtle rounded-md p-3 text-center">
      <div className="eyebrow text-2xs text-ink-500">{label}</div>
      <div className={`font-bold text-xl tabular-nums mt-1 ${color}`}>{value}</div>
    </div>
  );
}

// ============================================================
// Barcode labels
// ============================================================
function BarcodeLabelsCard() {
  const [format, setFormat] = useState<'avery_5160' | 'thermal_2x1'>('avery_5160');
  const [items, setItems] = useState<Array<{ productId: string; productName?: string; sku?: string; quantity: number }>>([]);
  const [productSearch, setProductSearch] = useState('');

  const prodQuery = useQuery({
    queryKey: ['products-label-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const mut = useMutation({
    mutationFn: () => inventoryApi.generateLabelsPdf(format, items.map((i) => ({
      productId: i.productId, quantity: i.quantity,
    }))),
    onSuccess: (blob) => {
      triggerDownload(blob, `labels-${Date.now()}.pdf`);
      toast.success('Labels PDF generated');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalLabels = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="card p-6 md:col-span-2">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
            <BarcodeIcon />
          </div>
          <div>
            <div className="eyebrow text-ink-500">Print</div>
            <h3 className="font-semibold text-lg text-ink-900 mt-0.5">Barcode labels</h3>
          </div>
        </div>
        {totalLabels > 0 && (
          <div className="bg-canvas-subtle rounded-xl px-4 py-2 text-right">
            <div className="eyebrow text-2xs text-ink-500">Total labels</div>
            <div className="font-display font-bold text-2xl text-ink-900 tabular-nums">{totalLabels}</div>
          </div>
        )}
      </div>
      <p className="text-sm text-ink-600 mb-5 leading-relaxed">
        Print barcode labels for inventory. Avery 5160 is 30 labels per US Letter sheet (laser printer). Thermal 2×1 is for Zebra-style single-label printers.
      </p>

      <div className="mb-5">
        <label className="label">Format</label>
        <select className="input max-w-md" value={format} onChange={(e) => setFormat(e.target.value as any)}>
          <option value="avery_5160">Avery 5160 · 30 labels per sheet (US Letter)</option>
          <option value="thermal_2x1">Thermal 2" × 1" · single label per page</option>
        </select>
      </div>

      <div className="eyebrow text-ink-500 mb-2">Items</div>
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-5">
        <div className="p-2 border-b border-ink-100 bg-canvas-subtle/40">
          <input
            className="input text-sm"
            placeholder="Search products to add…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
          {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
            <div className="mt-2 max-h-40 overflow-y-auto border border-ink-100 rounded-md bg-canvas-elevated">
              {(prodQuery.data?.data ?? []).map((p: any) => (
                <button
                  key={p.id}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-canvas-subtle/50 border-b border-ink-100 last:border-0 transition-colors"
                  onClick={() => {
                    setItems((prev) => [...prev, {
                      productId: p.id, productName: p.name, sku: p.sku, quantity: 1,
                    }]);
                    setProductSearch('');
                  }}
                >
                  <span className="font-mono text-ink-700">{p.sku}</span>
                  <span className="text-ink-900 ml-2">{p.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-center text-xs text-ink-400 italic">
            Search above to add products to the label run
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-right">Copies</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>
                    <div className="font-medium text-ink-900">{it.productName}</div>
                    <div className="font-mono text-2xs text-ink-500 mt-0.5">{it.sku}</div>
                  </td>
                  <td className="text-right">
                    <input
                      type="number" min="1" max="100"
                      className="input w-20 text-right tabular-nums"
                      value={it.quantity}
                      onChange={(e) => {
                        const v = Math.max(1, parseInt(e.target.value, 10) || 1);
                        setItems((prev) => prev.map((x, j) => j === i ? { ...x, quantity: v } : x));
                      }}
                    />
                  </td>
                  <td className="text-right w-12">
                    <button
                      className="text-danger hover:bg-red-50 w-8 h-8 rounded-md transition-colors text-xs"
                      onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <button
        className="btn-primary"
        disabled={items.length === 0 || mut.isPending}
        onClick={() => mut.mutate()}
      >
        {mut.isPending ? 'Generating…' : `Generate PDF${totalLabels > 0 ? ` · ${totalLabels} labels` : ''}`}
      </button>
    </div>
  );
}

// ============================================================
// Icons
// ============================================================
const iconProps = {
  width: 20, height: 20, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function DownloadIcon() { return <svg {...iconProps}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>; }
function UploadIcon()   { return <svg {...iconProps}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>; }
function BarcodeIcon()  { return <svg {...iconProps}><line x1="4" y1="5" x2="4" y2="19"/><line x1="7" y1="5" x2="7" y2="19"/><line x1="10" y1="5" x2="10" y2="19"/><line x1="14" y1="5" x2="14" y2="19"/><line x1="17" y1="5" x2="17" y2="19"/><line x1="20" y1="5" x2="20" y2="19"/></svg>; }
